import React, { Fragment, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useSelector } from 'react-redux';
import { navigate } from 'gatsby-link';

import { withSnackbar } from '@components/Snackbar';
import {
	makeDeleteRequest,
	makeGetRequest,
	makePostRequest,
	makePutRequest,
} from '@helpers/requests';
import { STORE_CATEGORIES, STORE_FIELD, STORE_FIELDS } from '@helpers/api';
import Command from '@components/Command';
import { Divider } from '@components/Grid';
import List from '@components/List';
import { format, parseISO } from 'date-fns';
import Loader from '@components/Loader';
import Card from '@components/Card';
import Panes from '@components/Panes';
import Switch from '@components/Switch';
import Checkboxes from '@components/Checkboxes';
import Text from '@components/Text';
import Form from '@views/Store/Fields/Field/Form';
import Wrapper from '@views/Store/Fields/Field/Wrapper';

const Field = ({ openSnackbar, id }) => {
	const user = useSelector(({ user }) => user);
	const [loading, setLoading] = useState(true);
	const [field, setField] = useState(null);
	const [allCategories, setAllCategories] = useState(null);

	const types = [
		{
			value: 'string',
			label: 'Short Answer',
			supports: {
				multiple: true,
			},
		},
		{
			value: 'paragraph',
			label: 'Paragraph',
			supports: {
				multiple: true,
			},
		},
		{
			value: 'radio',
			label: 'Multiple Choice',
		},
		{
			value: 'checkbox',
			label: 'Checkboxes',
		},
		{
			value: 'dropdown',
			label: 'Dropdown',
		},
		{
			value: 'html',
			label: 'HTML',
			supports: {
				multiple: true,
			},
		},
		{
			value: 'image',
			label: 'Image',
			supports: {
				multiple: true,
			},
		},
		{
			value: 'switch',
			label: 'Switch',
		},
		{
			value: 'collection',
			label: 'Collection',
		},
	];

	useEffect(() => {
		(async () => {
			try {
				if (id) {
					const { data: fieldData } = await makeGetRequest(STORE_FIELD(id));
					setField(fieldData);
				}

				const { data: categoriesData } = await makeGetRequest(STORE_CATEGORIES, {
					perPage: 1000,
					pageNum: 1,
				});
				setAllCategories(categoriesData?.results);
			} catch (error) {
				if (error !== 'cancelled') {
					openSnackbar(
						error?.errorMessage ?? 'An error occurred attempting to load this field.'
					);

					navigate(`/store/fields`);
				}
			} finally {
				setLoading(false);
			}
		})();
	}, []);

	const handleSubmit = async values => {
		const { label } = values;

		try {
			const buildData = ({
				label,
				type,
				hint,
				disabled,
				id,
				options,
				categories,
				multiple,
				optional,
				items,
				globalApplicability,
			}) => ({
				label,
				type,
				hint: hint || undefined,
				disabled: disabled || undefined,
				id,
				multiple: multiple || undefined,
				options:
					type === 'checkbox' || type === 'radio' || type === 'dropdown'
						? options
						: undefined,
				categories: globalApplicability ? undefined : categories,
				optional,
				items: type === 'collection' ? items?.map(item => buildData(item)) : undefined,
			});

			const data = buildData(values);

			const { data: fieldData } = !!field
				? await makePutRequest(STORE_FIELD(field?._id), data)
				: await makePostRequest(STORE_FIELDS, data);

			if (!id) {
				navigate(`/store/fields/${fieldData?._id}`);
			} else {
				setField(fieldData);
			}

			openSnackbar(`${label} field has been successfully ${!!field ? 'edited' : 'created'}.`);
		} catch (error) {
			console.log({ error });
			error !== 'cancelled' &&
				openSnackbar(
					error?.errorMessage ??
						`An error occurred ${!!field ? 'editing' : 'creating'} this field.`
				);
		}
	};

	const handleDelete = async () => {
		try {
			console.log({ id });
			await makeDeleteRequest(STORE_FIELD(id));
			openSnackbar(`${field?.label} field has been successfully deleted.`);
			navigate('/store/fields');
		} catch (error) {
			error !== 'cancelled' &&
				openSnackbar(error?.errorMessage ?? 'An error occurred deleting this field.');
		}
	};

	return (
		<Fragment>
			<Command>
				<Command.Breadcrumbs>
					<Command.Breadcrumbs.Breadcrumb text="Store" link="/store" />
					<Command.Breadcrumbs.Breadcrumb text="Fields" link="/store/fields" />
					<Command.Breadcrumbs.Breadcrumb text={field?.label ?? 'Field'} />
				</Command.Breadcrumbs>
			</Command>

			{loading ? (
				<Loader />
			) : (
				<Wrapper
					field={field}
					onSubmit={handleSubmit}
					onDelete={handleDelete}
					types={types}
				>
					{props => {
						const { values, setFieldValue } = props;

						const {
							id,
							label,
							type,
							hint,
							categories,
							multiple,
							globalApplicability,
							disabled,
							optional,
							items,
						} = values;

						const [creatingCollectionItem, setCreatingCollectionItem] = useState(false);
						const [activeCollectionItem, setActiveCollectionItem] = useState(null);

						// if field changes (from submission, reset nested collection item state).
						useEffect(() => {
							if (!field) return;
							setCreatingCollectionItem(false);
							setActiveCollectionItem(null);
						}, [field]);

						const handleCollectionSubmit = field => {
							const index = items?.findIndex(
								({ _id, collectionId }) =>
									_id === field?.collectionId ||
									collectionId === field?.collectionId
							);

							console.log({ index, items, field });

							setFieldValue(
								`items.${index >= 0 ? index : items?.length ?? 0}`,
								field
							);
							setActiveCollectionItem(null);
							setCreatingCollectionItem(false);
						};

						const handleCollectionDelete = async field => {
							try {
								setFieldValue(
									'items',
									items?.filter(
										({ _id, collectionId }) =>
											_id !== field?.collectionId &&
											collectionId !== field?.collectionId
									)
								);
								console.log({ field, items });

								setActiveCollectionItem(null);
								setCreatingCollectionItem(false);
							} catch (error) {}
						};

						const handleActiveCollectionItem = activeField => {
							setActiveCollectionItem(activeField);
							setCreatingCollectionItem(true);
						};

						return (
							<Panes>
								<Panes.Left>
									<Form
										types={types}
										allCategories={allCategories}
										onActiveCollectionItem={handleActiveCollectionItem}
										creatingCollectionItem={creatingCollectionItem}
										{...props}
									/>
								</Panes.Left>
								<Panes.Right>
									{!!creatingCollectionItem ? (
										<Fragment>
											<Panes.Title
												onBack={() => {
													setActiveCollectionItem(null);
													setCreatingCollectionItem(false);
												}}
											>
												<h3>
													{activeCollectionItem?.label
														? `Editing item - ${activeCollectionItem.label}`
														: 'New item'}
												</h3>
											</Panes.Title>

											<Wrapper
												nested
												field={activeCollectionItem}
												onSubmit={handleCollectionSubmit}
												onDelete={handleCollectionDelete}
												types={types}
											>
												{props => (
													<Form
														types={types.filter(
															type => type?.value !== 'collection'
														)}
														activeCollectionItem={activeCollectionItem}
														onActiveCollectionItem={
															handleActiveCollectionItem
														}
														{...props}
													/>
												)}
											</Wrapper>
										</Fragment>
									) : (
										<Fragment>
											<List>
												{(!!field || !!user?.permissions?.isAdmin) && (
													<List.Item>
														<List.Item.Column>
															<Text bold>Id</Text>
														</List.Item.Column>
														<List.Item.Column.Right important>
															<Text>{id}</Text>
														</List.Item.Column.Right>
													</List.Item>
												)}

												<List.Item>
													<List.Item.Column>
														<Text bold>Applicable</Text>
													</List.Item.Column>
													<List.Item.Column.Right important>
														<Text>
															{globalApplicability
																? 'Global'
																: 'Category'}
														</Text>
													</List.Item.Column.Right>
												</List.Item>
												<List.Item>
													<List.Item.Column>
														<Text bold>Label</Text>
													</List.Item.Column>
													<List.Item.Column.Right important>
														<Text>{label}</Text>
													</List.Item.Column.Right>
												</List.Item>
												<List.Item>
													<List.Item.Column>
														<Text bold>Hint</Text>
													</List.Item.Column>
													<List.Item.Column.Right important>
														<Text>{hint}</Text>
													</List.Item.Column.Right>
												</List.Item>
												<List.Item>
													<List.Item.Column>
														<Text bold>Type</Text>
													</List.Item.Column>

													<List.Item.Column.Right important>
														<Text>
															{
																types.find(
																	({ value }) => value === type
																)?.label
															}
														</Text>
													</List.Item.Column.Right>
												</List.Item>
												{types?.find(({ value }) => value === type)
													?.supports?.multiple && (
													<List.Item>
														<List.Item.Column>
															<Text bold>Multiple</Text>
														</List.Item.Column>
														<List.Item.Column.Right important>
															<List.Item.Checked checked={multiple} />
														</List.Item.Column.Right>
													</List.Item>
												)}
												<List.Item>
													<List.Item.Column>
														<Text bold>Disabled</Text>
													</List.Item.Column>
													<List.Item.Column.Right important>
														<List.Item.Checked checked={disabled} />
													</List.Item.Column.Right>
												</List.Item>
												<List.Item>
													<List.Item.Column>
														<Text bold>Optional</Text>
													</List.Item.Column>
													<List.Item.Column.Right important>
														<List.Item.Checked checked={optional} />
													</List.Item.Column.Right>
												</List.Item>
												{!!field && (
													<Fragment>
														<List.Item key="timestamp">
															<List.Item.Column>
																<Text bold>Created</Text>
															</List.Item.Column>
															<List.Item.Column.Right important>
																<Text>
																	{!!field?.timestamp &&
																		format(
																			parseISO(
																				field?.timestamp
																			),
																			'PPP p'
																		)}
																</Text>
															</List.Item.Column.Right>
														</List.Item>
													</Fragment>
												)}
											</List>

											{!!categories && !!categories.length && (
												<Fragment>
													<Divider />
													<Card>
														<Card.Title text="Applicable Categories" />
														<Card.Content>
															<List>
																{allCategories
																	.filter(({ _id }) =>
																		categories.some(
																			category =>
																				category === _id
																		)
																	)
																	.map(({ _id, id, name }) => (
																		<List.Item
																			key={_id}
																			link={`/store/categories/${_id}`}
																		>
																			<List.Item.Title
																				small
																				text={name}
																			/>
																			<List.Item.Description
																				text={id}
																			/>
																		</List.Item>
																	))}
															</List>
														</Card.Content>
													</Card>
												</Fragment>
											)}
										</Fragment>
									)}
								</Panes.Right>
							</Panes>
						);
					}}
				</Wrapper>
			)}
		</Fragment>
	);
};

export default withSnackbar(Field);
